import { useEffect, useState } from "react";
import { TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Divider, Chip } from "@mui/material";

import axios from "axios";
import Swal from "sweetalert2";

export default function EditUtilityLicenseForm({ open, handleClose, rowData, fetchData }) {
    const [editData, setEditData] = useState({});
    const [errors, setErrors] = useState({ expiry_date: false, company_name: false, contact: false, mac_address: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (rowData) {
            setEditData({ ...rowData });
        }

        if (open) {
            setErrors({ expiry_date: false, company_name: false, contact: false, mac_address: false });
            setHelperText({});
            setErrorMessage("");
        }
    }, [rowData, open]);

    if (!rowData) return null;

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const clearError = async (field) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
    };

    const validate = async (field, value) => {
        if (!value || value === "") {
            await setError(field, "This field is required.");
            return;
        }

        if (field === "expiry_date" && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            await setError("expiry_date", "Expiry Date must be in YYYY-MM-DD format.");
            return;
        }

        await clearError(field);
    };

    const handleEditUtilityLicense = async () => {
        let isValid = true;
        const payload = {
            expiry_date: editData.expiry_date || "",
            company_name: editData.company_name || "",
            contact: editData.contact || "",
            mac_address: editData.mac_address || "",
        };

        for (const [field, value] of Object.entries(payload)) {
            await validate(field, value);
            if (!value) {
                isValid = false;
            }
        }

        if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.expiry_date)) {
            isValid = false;
        }

        if (!isValid) {
            setErrorMessage("Please fill all the required fields correctly.");
            return;
        }

        try {
            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/utility-license/${encodeURIComponent(rowData.license_key)}`, payload);

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Utility license information updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the utility license information. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Utility License Information</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Utility License Details" />
                </Divider>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="license_key"
                    label="License Key"
                    type="text"
                    fullWidth
                    required
                    value={rowData.license_key}
                    InputProps={{ readOnly: true }}
                    sx={{ background: "#e7e7e79e" }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="expiry_date"
                    label="Expiry Date"
                    type="date"
                    fullWidth
                    required
                    value={editData.expiry_date || ""}
                    error={errors.expiry_date}
                    helperText={helperText.expiry_date}
                    onChange={(e) => {
                        setEditData({ ...editData, expiry_date: e.target.value });
                        validate("expiry_date", e.target.value);
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="company_name"
                    label="Company Name"
                    type="text"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    value={editData.company_name || ""}
                    error={errors.company_name}
                    helperText={helperText.company_name}
                    onChange={(e) => {
                        const value = e.target.value || "";
                        setEditData({ ...editData, company_name: value });
                        validate("company_name", value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="contact"
                    label="Contact"
                    type="text"
                    fullWidth
                    required
                    value={editData.contact || ""}
                    error={errors.contact}
                    helperText={helperText.contact}
                    onChange={(e) => {
                        setEditData({ ...editData, contact: e.target.value || "" });
                        validate("contact", e.target.value);
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="mac_address"
                    label="Mac Address"
                    type="text"
                    fullWidth
                    required
                    value={editData.mac_address || ""}
                    error={errors.mac_address}
                    helperText={helperText.mac_address}
                    onChange={(e) => {
                        const value = e.target.value || "";
                        setEditData({ ...editData, mac_address: value });
                        validate("mac_address", value);
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleEditUtilityLicense} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Update
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
